import React, { useEffect, useRef, useState } from 'react';

import {
  SelectedOption,
  ArrowIcon,
  SelectedOptionLabel,
  Container,
  Placeholder
} from './CountrySelect.styled';
import ArrowDown from 'assets/icons/arrow_down.svg?react';
import ArrowUp from 'assets/icons/arrow_up.svg?react';
import { DropdownMenu } from 'components/v2/Inputs/DropdownMenu/DropdownMenu';
import { Option as DropdownOption } from 'components/v2/Inputs/DropdownMenu/DropdownMenu.types';
import { Option as SelectInputOption } from 'components/v2/Inputs/SelectInput/SelectInput.types';
import { escapeRegex } from 'kb-shared/utilities/regex';

import { Props } from './CountrySelect.types';
import { getInputStates } from '../../utils';

export const CountrySelect = <T extends SelectInputOption>({
  id,
  labelId,
  dropdownId,
  status = 'default',
  searchPlaceholder,
  selected,
  placeholder,
  onChange,
  onFocus,
  onBlur,
  unstyled = false,
  options,
  selectedOptionFormatter
}: Props): JSX.Element => {
  const [isDropDownOpened, setIsDropDownOpened] = useState<boolean>(false);
  const [initialFocusTarget, setInitialFocusTarget] = useState<'search' | 'selectedOption'>(
    'search'
  );
  const [selectionAnnouncement, setSelectionAnnouncement] = useState('');
  const comboboxRef = useRef<HTMLDivElement>(null);
  const dropDownMenuRef = useRef<HTMLDivElement>(null);
  const { isDisabled } = getInputStates(status);
  const isFilled = Boolean(selected);

  const closeDropdown = ({ restoreFocus = false }: { restoreFocus?: boolean } = {}) => {
    setIsDropDownOpened(false);
    onBlur?.();

    if (restoreFocus) {
      requestAnimationFrame(() => {
        comboboxRef.current?.focus();
      });
    }
  };

  const openDropdown = (focusTarget: 'search' | 'selectedOption' = 'search') => {
    setInitialFocusTarget(focusTarget);
    setIsDropDownOpened(true);
    onFocus?.();
  };

  const clickOutside = (e: Event) => {
    if (
      !dropDownMenuRef.current?.contains(e.target as Node) &&
      !comboboxRef.current?.contains(e.target as Node)
    ) {
      closeDropdown();
    }
  };

  const closeOnFocusLeave = (e: FocusEvent) => {
    if (
      !dropDownMenuRef.current?.contains(e.target as Node) &&
      !comboboxRef.current?.contains(e.target as Node)
    ) {
      closeDropdown();
    }
  };

  const toggle = (focusTarget: 'search' | 'selectedOption' = 'search') => {
    if (isDisabled) return;

    if (isDropDownOpened) {
      closeDropdown();
      return;
    }

    openDropdown(focusTarget);
  };

  const search = (options: Array<DropdownOption>, term: string): Array<DropdownOption> => {
    if (!term) {
      return options;
    }

    const regex = new RegExp(`.*${escapeRegex(term)}.*`, 'i');
    const filteredByTerm = options.filter(option => regex.test(option.label));

    return filteredByTerm;
  };

  const select = (option: T) => {
    const selectedOption = selectedOptionFormatter ? selectedOptionFormatter(option) : option;
    setSelectionAnnouncement(`Selected ${selectedOption.label}`);
    closeDropdown({ restoreFocus: true });
    onChange(selectedOption);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (isDisabled) return;

    if (event.key === 'Enter' || event.key === ' ' || event.key === 'ArrowDown') {
      event.preventDefault();

      if (!isDropDownOpened) {
        openDropdown('selectedOption');
      }
    } else if (event.key === 'Escape' && isDropDownOpened) {
      event.preventDefault();
      closeDropdown();
    }
  };

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    // VoiceOver activation can dispatch a synthesized click without pointer detail.
    toggle(event.detail === 0 ? 'selectedOption' : 'search');
  };

  useEffect(() => {
    if (!isDropDownOpened) return;

    document.addEventListener('mousedown', clickOutside);
    document.addEventListener('focusin', closeOnFocusLeave);

    return () => {
      document.removeEventListener('mousedown', clickOutside);
      document.removeEventListener('focusin', closeOnFocusLeave);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDropDownOpened]);

  return (
    <Container $unstyled={unstyled}>
      <span className="sr-only" role="status" aria-live="polite">
        {selectionAnnouncement}
      </span>
      <SelectedOption
        id={id}
        ref={comboboxRef}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        $isDropDownOpened={isDropDownOpened}
        role="combobox"
        aria-labelledby={labelId}
        aria-expanded={isDropDownOpened}
        aria-controls={dropdownId}
        aria-autocomplete="none"
        aria-disabled={isDisabled}
        tabIndex={isDisabled ? -1 : 0}
        $status={status}
        $unstyled={unstyled}
        $isFilled={isFilled}
      >
        <SelectedOptionLabel title={selected?.label}>
          {selected?.label ? (
            selected?.label
          ) : (
            <Placeholder $status={status}>{placeholder}</Placeholder>
          )}
        </SelectedOptionLabel>
        <ArrowIcon aria-hidden="true">{isDropDownOpened ? <ArrowUp /> : <ArrowDown />}</ArrowIcon>
      </SelectedOption>

      {isDropDownOpened && (
        <DropdownMenu
          id={dropdownId}
          ref={dropDownMenuRef}
          options={options}
          selected={selected ? [selected] : undefined}
          onSelect={option => select(option as T)}
          onClose={closeOptions => closeDropdown(closeOptions)}
          searchPlaceholder={searchPlaceholder}
          searchFn={search}
          searchable
          initialFocusTarget={initialFocusTarget}
        />
      )}
    </Container>
  );
};
